import { useFeatureIsOn } from '@growthbook/growthbook-react'
import Hotjar from '@hotjar/browser'
import {
  USER_ROLE_NAMES,
  usePantryCanSwitchWorkspace,
  usePantryNavData,
  usePantryWarehouses,
} from '@pantry/features'
import { Navbar } from '@pantry/ui'
import type { NavSection } from '@pantry/ui'
import {
  Announcement,
  AnnouncementProvider,
  NotificationProvider,
  NotificationType,
} from 'bs-unified-ui'
import isEqual from 'lodash/isEqual'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Navigate,
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'
import logoColorSimpleTopBarUrl from '~/assets/icons/logo_color_simple_topBar.svg'
import Version from '~/assets/version.json'
import RoleGuard from '~/components/Guard/PantryRoleGuard'
import StoreGuard from '~/components/Guard/StoreGuard'
import BackToTopButton from '~/components/common/BackToTop'
import growthbook from '~/configs/growthBook'
import {
  HOTJAR_SITE_ID,
  PENDO_API_KEY,
  REACT_APP_COM_HUB_API_URL,
  REACT_APP_NOTIFICATION_URL,
} from '~/constants/environment'
import { FLAGS } from '~/constants/flags'
import {
  ADMIN_ROUTES,
  POINT_AND_REWARDS_ROUTERS,
  PROMOTION_ROUTES,
} from '~/constants/router'
import { TIMEZONE_SETTING } from '~/constants/settings'
import { VANCOUVER_TZ } from '~/constants/share'
import { STORE_TYPES } from '~/constants/store'
import { USER_DEVICE_ZONE } from '~/constants/timezone'
import Guard from '~/modules/auth/components/Guard'
import { isActivePointReward, isValidUser } from '~/modules/auth/conditions'
import { useAuth } from '~/modules/auth/redux/hook'
import authApi from '~/modules/auth/services'
import { useSyncedSignOut } from '~/modules/auth/utils/syncedSignOut'
import fbAuth from '~/modules/firebase'
import { useCanfleetNotification } from '~/pages/Account/Notification/hooks/useCanfleetNotification'
import { useGetMe } from '~/pages/Account/Notification/hooks/useGetMe'
import useNotification from '~/pages/Account/Notification/hooks/useNotification'
import { useCheckCanfleetUser } from '~/pages/Settings/hooks/useCheckCanfleetUser'
import { useFetchingIncompletedOrders } from '~/redux/hooks/cantecFulfillment'
import { useIsSyncedStore, useStoreType } from '~/redux/hooks/store'
import { useActiveStore } from '~/redux/hooks/store'
import { useIsAccessibleAllLocation, useUser } from '~/redux/hooks/user'
import { urlWithStoreID } from '~/utils/common'
import { useClaims } from '~/utils/hooks/useClaims'
import useGATracking from '~/utils/hooks/useGATracking'
import { useNotificationSound } from '~/utils/hooks/useNotificationSound'
import { useUserSession } from '~/utils/hooks/useUserSession'
import logger from '~/utils/logger'
import { goToTop } from '~/utils/scrollUp'
import PosBadge from './nav/PosBadge'
import { buildPantrySections } from './nav/buildSections'
import './style.css'

export const USER_ROLES = {
  AGENT: 'agent',
  CHATSO_ADMIN: 'coach',
  ADMIN: 'admin',
  CUSTOM: 'custom_role',
  MARKETING_MANAGER: 'marketing_manager',
  MARKETING_AGENT: 'marketing_agent',
}

// Fallback when version.json has no version_number (set by makeVer.js at build)
const APP_VERSION = 'v1.0.39'
const versionNumber = (Version as { version_number?: string }).version_number
const DISPLAY_VERSION = versionNumber ? `v${versionNumber}` : APP_VERSION

interface PantryAdminLayoutProps {
  children: React.ReactNode
}

const PantryAdminLayout = ({ children }: PantryAdminLayoutProps) => {
  const isCF1997Enabled = useFeatureIsOn(FLAGS.CF_1997)
  const isNotiV2 = useFeatureIsOn(FLAGS.CS_9035)

  const location = useLocation()
  useSyncedSignOut()
  useGATracking()
  const { storeId } = useParams()
  const navigate = useNavigate()
  const { actions, authState } = useAuth()
  const {
    actions: userActions,
    orgId,
    user: { user_settings },
  } = useUser()
  const pendoRef = useRef('')
  const storeType = useStoreType(storeId)
  const [disable, setDisable] = useState(false)
  const isSyncedStore = useIsSyncedStore()
  const activeStore = useActiveStore()
  const {
    user: { user_settings: userSettings },
  } = useUser()

  const { claims } = useClaims()
  const { data: checkCanfleetUser } = useCheckCanfleetUser(
    storeId,
    claims?.email,
    !!claims?.is_canfleet,
  )
  const isCanfleet = !!checkCanfleetUser?.userExistsInCf

  const { data: userMe, refetch: refetchUser } = useGetMe()

  const [sessionData, setSessionData] = useUserSession()
  const warehouses = usePantryWarehouses()
  const isAccessibleAllLocation = useIsAccessibleAllLocation()
  const locationOptions = useMemo<
    Array<{ value: string | number | undefined; label: string; name?: string }>
  >(() => {
    const initial = isAccessibleAllLocation
      ? [{ value: -1, label: 'All locations', name: 'All locations' }]
      : []
    const mapped = warehouses
      .map((wh) => ({ value: wh.id, label: wh.name }))
      .sort((a, b) => a.label.localeCompare(b.label))
    return [...initial, ...mapped]
  }, [warehouses, isAccessibleAllLocation])

  const { data: cfNotificationSettings, refetch: refetchCanfleetNotification } =
    useCanfleetNotification(checkCanfleetUser?.cfOrgId, isCanfleet)

  const { data: notificationData, actions: notificationSettingActions } =
    useNotification(storeId)

  const currentWorkSpaceUserRole = useMemo(() => {
    return userMe?.workspaces?.find(
      (ws: any) => ws?.workspaceId?.id === activeStore?.csWsId,
    )
  }, [userMe, storeId])

  const timezoneNameUserSetting = useMemo(() => {
    if (userSettings?.tz_setting === TIMEZONE_SETTING.AUTO_DETECT) {
      return USER_DEVICE_ZONE
    }
    if (userSettings?.tz_setting === TIMEZONE_SETTING.MANUALLY) {
      return userSettings?.tz_timezone
    }
    return VANCOUVER_TZ
  }, [userSettings?.tz_setting, userSettings?.tz_timezone])

  const soundConfig = useMemo(() => {
    if (isNotiV2)
      return {
        ...userMe?.config?.chatNotificationSound?.soundConfig,
        delivery: {
          mute: cfNotificationSettings?.mute,
          url: cfNotificationSettings?.url,
          repeatingInterval: cfNotificationSettings?.repeatingInterval,
        },
      }

    return userMe?.config?.chatNotificationSound?.soundConfig
  }, [
    userMe?.config?.chatNotificationSound?.soundConfig,
    cfNotificationSettings?.mute,
    cfNotificationSettings?.url,
    cfNotificationSettings?.repeatingInterval,
    isNotiV2,
  ])

  const canAccessLiveChat = useMemo(() => {
    if (currentWorkSpaceUserRole?.role === USER_ROLES.CUSTOM)
      return currentWorkSpaceUserRole?.customRoles?.chatso_live_chat
    return [
      USER_ROLES.ADMIN,
      USER_ROLES.CHATSO_ADMIN,
      USER_ROLES.AGENT,
    ].includes(currentWorkSpaceUserRole?.role)
  }, [currentWorkSpaceUserRole])

  const canAccessOrder = useMemo(() => {
    if (currentWorkSpaceUserRole?.role === USER_ROLES.CUSTOM)
      return currentWorkSpaceUserRole?.customRoles?.breadstack_order
    return [
      USER_ROLES.ADMIN,
      USER_ROLES.CHATSO_ADMIN,
      USER_ROLES.AGENT,
      USER_ROLES.MARKETING_MANAGER,
      USER_ROLES.MARKETING_AGENT,
    ].includes(currentWorkSpaceUserRole?.role)
  }, [currentWorkSpaceUserRole])

  const triggerEvents = useMemo(
    () => ({
      new_pending_chat:
        canAccessLiveChat &&
        userMe?.config?.chatNotificationSound?.pendingChat?.enabled,
      new_unread_message:
        canAccessLiveChat &&
        userMe?.config?.chatNotificationSound?.newMessage?.enabled,
      customer_left:
        canAccessLiveChat &&
        userMe?.config?.chatNotificationSound?.customerLeft?.enabled,
      missed_chat:
        canAccessLiveChat &&
        userMe?.config?.chatNotificationSound?.missedChat?.enabled,
      new_incoming_order: canAccessOrder && notificationData?.new_order,
      cancelled_order: canAccessOrder && notificationData?.order_cancelled,
      task_created: cfNotificationSettings?.task_update?.enable,
      task_completed: cfNotificationSettings?.task_update?.enable,
      task_failed: cfNotificationSettings?.task_update?.enable,
      failed_to_pick_up: cfNotificationSettings?.task_update?.enable,
      success_to_pick_up: cfNotificationSettings?.task_update?.enable,
      failed_to_return: cfNotificationSettings?.task_update?.enable,
      success_to_return: cfNotificationSettings?.task_update?.enable,
      import_task: cfNotificationSettings?.import_csv?.enable,
      new_driver: cfNotificationSettings?.new_driver?.enable,
    }),
    [
      userMe?.config?.chatNotificationSound?.pendingChat?.enabled,
      userMe?.config?.chatNotificationSound?.newMessage?.enabled,
      userMe?.config?.chatNotificationSound?.customerLeft?.enabled,
      userMe?.config?.chatNotificationSound?.missedChat?.enabled,
      notificationData?.new_order,
      notificationData?.order_cancelled,
      canAccessLiveChat,
      canAccessOrder,
      cfNotificationSettings,
    ],
  )

  const warehousesConfig = useMemo(() => {
    const listConfig = notificationData?.warehouses?.reduce((acc, w) => {
      if ('all' in w) {
        if (w?.all) return [...acc, 'all']
        return acc
      }
      return [...acc, w?.warehouse_id]
    }, [])
    return listConfig
  }, [notificationData?.warehouses])

  // Initialize notification sound and get play functions
  const { playNewTaskSound, playNewOrderSound } = useNotificationSound()

  useEffect(() => {
    const isContainLocation =
      Array.isArray(locationOptions) &&
      locationOptions.some(
        (item) => item.value === user_settings?.default_location,
      )
    if (
      user_settings?.default_location &&
      isContainLocation &&
      !sessionData.location_id
    ) {
      setSessionData({
        ...sessionData,
        location_id: user_settings?.default_location,
      })
    }
  }, [user_settings?.default_location, locationOptions, sessionData])

  const hotjarRef = useRef(null)
  const firebaseUserId = authApi.getUserFirebaseId()

  useEffect(() => {
    const params = {
      id: authState?.data?.userId,
      email: authState?.data?.userEmail,
      full_name: authState?.data?.fullName,
      org_id: orgId,
      org_name: authState?.data?.orgs ? authState?.data?.orgs[orgId]?.name : '',
      store_id: storeId,
      role: authState?.data?.stores
        ? authState?.data?.stores[storeId]?.roleId
        : '',
    }

    if (firebaseUserId && HOTJAR_SITE_ID) {
      if (isEqual(hotjarRef.current, params)) return
      hotjarRef.current = params
      Hotjar.identify(firebaseUserId, params)
    }
  }, [
    firebaseUserId,
    authState?.data?.userEmail,
    authState?.data?.fullName,
    authState?.data?.userId,
    authState?.data?.orgs,
    authState?.data?.stores,
    orgId,
    storeId,
  ])

  useEffect(() => {
    const params = {
      visitor: {
        id: authState?.data?.userId,
        email: authState?.data?.userEmail,
        full_name: authState?.data?.fullName,
        role: authState?.data?.stores
          ? authState?.data?.stores[storeId]?.roleId
          : '',
      },
      account: {
        id: orgId,
        name: authState?.data?.orgs ? authState?.data?.orgs[orgId]?.name : '',
        is_paying: false,
      },
    }
    growthbook.setAttributes({
      id: authState?.data?.userId,
      email: authState?.data?.userEmail,
      role: authState?.data?.stores
        ? authState?.data?.stores[storeId]?.roleId
        : '',
      orgId,
      storeId,
      storeType: activeStore?.storeType,
      firebaseUserId: firebaseUserId || authState?.data?.userId,
    })
    if (isEqual(pendoRef.current, params)) return
    if (PENDO_API_KEY) {
      //@ts-ignore
      pendoRef.current = params
      //@ts-ignore
      window.pendo?.initialize(params)
    }
  }, [authState, orgId, storeId, activeStore?.storeType, firebaseUserId])

  useEffect(() => {
    const { pathname } = location
    if (new RegExp(/media/g).test(pathname)) {
      goToTop('content-folder')
      goToTop('mediaListFile')
      setDisable(true)
    } else if (new RegExp(/(promotion|custom-report)/g).test(pathname)) {
      setDisable(true)
    } else {
      setDisable(false)
    }
  }, [location])

  useEffect(() => {
    logger.info('Check auth state at pantry admin layout')
    actions.checkAuthState()
  }, [])

  useEffect(() => {
    if (
      orgId &&
      storeId &&
      storeType !== STORE_TYPES.SHOPIFY &&
      isSyncedStore
    ) {
      actions.getPlugins({
        onSuccess: (plugins) => {
          const isInPoint = !!matchPath(
            {
              path: `${ADMIN_ROUTES.STORE}${POINT_AND_REWARDS_ROUTERS.POINT}/:id?`,
            },
            location.pathname,
          )
          if (isInPoint && !isActivePointReward()({ plugins })) {
            navigate(urlWithStoreID(PROMOTION_ROUTES.PROMOTION_RAW, storeId), {
              replace: true,
            })
          }
        },
        onError: () => {},
      })
    }
  }, [orgId, storeId, storeType, isSyncedStore])

  const handleNotification = useCallback(
    (notification) => {
      if (notification?.event === NotificationType.SYNC_SETTINGS) {
        notificationSettingActions.getNotificationSettings([storeId])
        refetchUser?.()
        if (isCanfleet && checkCanfleetUser?.cfOrgId) {
          refetchCanfleetNotification?.()
        }
      }
    },
    [notificationSettingActions, refetchUser, storeId, isCanfleet],
  )

  useEffect(() => {
    if (orgId) {
      userActions.getOrg({ organizations_list: [{ organization_id: orgId }] })
    }
    if (storeId) {
      userActions.getStores({ stores_list: [{ store_id: storeId }] })
    }
  }, [orgId, storeId])

  useFetchingIncompletedOrders(storeId)

  const navApi = usePantryNavData()
  const canSwitchWorkspace = usePantryCanSwitchWorkspace()

  const navSections = useMemo<NavSection[]>(
    () =>
      buildPantrySections({
        api: navApi,
        storeId,
        pathname: location.pathname,
        navigate,
      }),
    [navApi, storeId, location.pathname, navigate],
  )

  const navUser = useMemo(() => {
    const fullName = (authState?.data?.fullName || '').trim()
    const parts = fullName.split(' ').filter(Boolean)
    const initials =
      parts.length >= 2
        ? `${parts[0][0]}${parts[parts.length - 1][0]}`
        : fullName.slice(0, 2)
    const roleId = authState?.data?.stores?.[storeId]?.roleId
    const roleLabel = USER_ROLE_NAMES[roleId] || ''
    return {
      initials: initials.toUpperCase(),
      name: fullName,
      role: roleLabel,
      onAccountClick: () =>
        navigate(`/s/${storeId}${ADMIN_ROUTES.ACCOUNT}/my-account`),
      ...(canSwitchWorkspace && {
        onSwitchWorkspaceClick: () =>
          navigate(
            `/workspace-switcher/${activeStore?.csWsId || storeId || ''}`,
          ),
      }),
      onSignOutClick: () => actions.signOut(),
    }
  }, [
    authState?.data,
    navigate,
    storeId,
    activeStore?.csWsId,
    actions,
    canSwitchWorkspace,
  ])

  return (
    <Guard accessWhen={isValidUser} fallbackComp={<Navigate to="/login" />}>
      <AnnouncementProvider
        apiUrl={REACT_APP_COM_HUB_API_URL}
        orgId={orgId}
        enabled={!!orgId && isCF1997Enabled}
        getFirebaseToken={fbAuth.getToken}
      >
        <NotificationProvider
          config={{
            apiBaseUrl: REACT_APP_NOTIFICATION_URL,
            streamEndpoint: '/notifications/stream',
            notificationEndpoint: '/notifications',
            bsOrgId: orgId,
            auth: {
              getFirebaseToken: fbAuth.getToken,
              sessionTokenEndpoint: '/notifications/stream/token',
            },
            timezone: timezoneNameUserSetting,
            navigate: navigate,
            onNotification: handleNotification,
            soundConfig,
            triggerEvents,
            warehousesConfig,
            isV2: isNotiV2,
          }}
        >
          <Announcement renderGlobalBanner={true} renderPopup={true} />
          <div className="pantry-layout__body">
            <Navbar
              sections={navSections}
              storeName={activeStore?.storeName}
              storeUrl={activeStore?.storeUrl}
              logoMarkSrc={logoColorSimpleTopBarUrl}
              version={DISPLAY_VERSION}
              headerBadge={
                <PosBadge
                  storeType={activeStore?.storeType}
                  companyId={activeStore?.companyId}
                  useMockData={activeStore?.useMockData}
                />
              }
              user={navUser}
            />
            <div className="pantry-layout__content">
              <BackToTopButton showBelow={500} disable={disable} />
              <StoreGuard>
                <RoleGuard>
                  <div
                    id="content"
                    className="pantry-layout__children customized-scrollbar"
                  >
                    {authState.status === 'SUCCESS' && children}
                  </div>
                </RoleGuard>
              </StoreGuard>
            </div>
          </div>
        </NotificationProvider>
      </AnnouncementProvider>
    </Guard>
  )
}

PantryAdminLayout.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
}

export default PantryAdminLayout
